import { useCallback, useLayoutEffect, useRef, useState, type MutableRefObject, type RefObject } from 'react';

/**
 * Focus across the mail screen's three panes (#205, doc 06 "Focus management
 * in the three-pane layout").
 *
 * - **Pane cycling.** F6 / Shift+F6 (and Ctrl/⌘+Alt+→/←) move focus folders →
 *   list → message → folders, and say which pane focus landed in.
 * - **Stacked open.** Where the list and the message cannot both be on screen
 *   (a phone, a narrow window, the reading pane "off"), opening a message from
 *   the list moves focus into it: the list it was on has just been hidden, and
 *   focus on a hidden element falls to `<body>`.
 * - **Return.** Closing the message puts focus back on the list, on the row
 *   that was open — unless focus has already gone somewhere on purpose (the
 *   search box, a folder just clicked).
 * - **"Opened: <subject>".** A message arriving in the pane is said once, in a
 *   polite region of its own (see `announcement`).
 *
 * Panes are found by `data-pane` (`folders` on the sidebar's tree, `list` and
 * `message` on MailView's two sections), so the shell needs no part in it.
 */

export type PaneName = 'folders' | 'list' | 'message';

const ORDER: readonly PaneName[] = ['folders', 'list', 'message'];

/** What a pane is called when focus lands in it: the regions' own labels. */
const SPOKEN: Record<PaneName, string> = {
  folders: 'Folders',
  list: 'Message list',
  message: 'Reading pane',
};

/** Shown means no ancestor is `display: none` (the stacked layout's way of hiding a pane). */
function shown(element: Element | null): boolean {
  for (let node = element; node !== null; node = node.parentElement) {
    if (getComputedStyle(node).display === 'none') return false;
  }
  return element !== null;
}

function paneOf(element: Element | null): PaneName | undefined {
  const pane = element?.closest('[data-pane]')?.getAttribute('data-pane');
  return pane === 'folders' || pane === 'list' || pane === 'message' ? pane : undefined;
}

/** Focus fell nowhere: on `<body>`, or there is no active element at all. */
function focusIsLost(): boolean {
  const active = document.activeElement;
  return active === null || active === document.body || active === document.documentElement;
}

export interface PaneFocusOptions {
  gridRef: RefObject<HTMLElement | null>;
  listPaneRef: RefObject<HTMLElement | null>;
  readingPaneRef: RefObject<HTMLElement | null>;
  /** The open message's id, from the route. */
  messageId: string | undefined;
  /**
   * One pane at a time as far as JS can tell: handheld, or the reading pane
   * "off". The 60–75rem container-query stacking is read from the list
   * pane's computed style as well.
   */
  stacked: boolean;
  /** The open message's subject once known (the row's, else the detail's). */
  openedSubject: string | undefined;
  /**
   * Set by MailView's `openMessage` (a click or Enter on a row) and consumed
   * here: only an open the list asked for moves focus. A message restored by
   * the route (a reload, a link, Back) is not the person leaving the list.
   */
  openFromListRef: MutableRefObject<boolean>;
}

export interface PaneFocus {
  /** F6 / Shift+F6: the next or previous pane on screen. */
  cyclePane: (direction: 1 | -1) => void;
  /** The navigation live region's text (pane names, "Opened: …"). */
  announcement: string;
}

export function usePaneFocus({
  gridRef,
  listPaneRef,
  readingPaneRef,
  messageId,
  stacked,
  openedSubject,
  openFromListRef,
}: PaneFocusOptions): PaneFocus {
  /*
   * The text, and a flip that appends a no-break space every other time: the
   * same sentence twice (F6 back into the same pane) is a DOM change, so a
   * screen reader reads it again.
   */
  const [spoken, setSpoken] = useState<{ text: string; flip: boolean }>({ text: '', flip: false });
  const say = useCallback((text: string) => {
    setSpoken((previous) => ({ text, flip: !previous.flip }));
  }, []);

  const messageOpen = messageId !== undefined;

  const element = useCallback(
    (pane: PaneName): HTMLElement | null => {
      if (pane === 'list') return listPaneRef.current;
      if (pane === 'message') return readingPaneRef.current;
      return document.querySelector<HTMLElement>('[data-pane="folders"]');
    },
    [listPaneRef, readingPaneRef],
  );

  const listHidden = useCallback(
    () => (stacked && messageOpen) || !shown(listPaneRef.current),
    [listPaneRef, messageOpen, stacked],
  );

  const available = useCallback(
    (pane: PaneName): boolean => {
      if (pane === 'message') return messageOpen && shown(readingPaneRef.current);
      if (pane === 'list') return !listHidden();
      return shown(element('folders'));
    },
    [element, listHidden, messageOpen, readingPaneRef],
  );

  const focusPane = useCallback(
    (pane: PaneName) => {
      if (pane === 'list') {
        gridRef.current?.focus();
        return;
      }
      if (pane === 'message') {
        // The section itself, not its first control: that changes with the
        // loading state, and is "Back, button" on a phone. The region's name
        // and then its content is what a screen reader should start with.
        readingPaneRef.current?.focus();
        return;
      }
      const tree = element('folders');
      // The open folder is the tree's natural stop; with none (a label view,
      // a search), the tree itself.
      const selected = tree?.querySelector<HTMLElement>(
        '[role="treeitem"][aria-selected="true"] .folder-tree__main:not(:disabled)',
      );
      (selected ?? tree)?.focus();
    },
    [element, gridRef, readingPaneRef],
  );

  const cyclePane = useCallback(
    (direction: 1 | -1) => {
      const from = paneOf(document.activeElement);
      const panes = ORDER.filter(available);
      if (panes.length === 0) return;
      let next: PaneName;
      if (from === undefined) {
        // Focus is nowhere in particular: start at the list, the screen's
        // main content (where the skip link lands too).
        next = panes.includes('list') ? 'list' : (panes[0] ?? 'list');
      } else {
        const at = ORDER.indexOf(from);
        next = from;
        for (let step = 1; step <= ORDER.length; step += 1) {
          const candidate = ORDER[(at + direction * step + ORDER.length * 2) % ORDER.length];
          if (candidate !== undefined && panes.includes(candidate)) {
            next = candidate;
            break;
          }
        }
      }
      focusPane(next);
      say(SPOKEN[next]);
    },
    [available, focusPane, say],
  );

  const previousMessageId = useRef(messageId);
  // Layout effect: before paint, so focus never visibly sits on <body>.
  useLayoutEffect(() => {
    const before = previousMessageId.current;
    previousMessageId.current = messageId;
    if (before === messageId) return;
    if (messageId !== undefined) {
      const fromList = openFromListRef.current;
      openFromListRef.current = false;
      if (!fromList || before !== undefined) return;
      const active = document.activeElement;
      const wasInList = focusIsLost() || listPaneRef.current?.contains(active) === true;
      if (wasInList && listHidden()) readingPaneRef.current?.focus({ preventScroll: true });
      return;
    }
    // Closed. Focus that was in the reading pane went with its content.
    if (before === undefined) return;
    const active = document.activeElement;
    if (focusIsLost() || readingPaneRef.current?.contains(active) === true) {
      gridRef.current?.focus({ preventScroll: true });
    }
  }, [gridRef, listHidden, listPaneRef, messageId, openFromListRef, readingPaneRef]);

  /*
   * "Opened: <subject>", once per message. Never for the message the screen
   * was loaded on (nobody opened it), and never again for the same id when the
   * list refetches or the stream patches it. Adjusted during render (React's
   * "storing information from previous renders"), not in an effect, so the
   * sentence lands in the same commit as the message.
   */
  const [announcedId, setAnnouncedId] = useState(messageId);
  if (messageId !== announcedId && (messageId === undefined || openedSubject !== undefined)) {
    setAnnouncedId(messageId);
    if (messageId !== undefined) {
      const text = `Opened: ${openedSubject?.trim() || '(no subject)'}`;
      setSpoken((previous) => ({ text, flip: !previous.flip }));
    }
  }

  return {
    cyclePane,
    announcement: spoken.text === '' ? '' : `${spoken.text}${spoken.flip ? ' ' : ''}`,
  };
}
